
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-shield-check-line mr-2"></i>
            Legal Terms
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Website Terms of Use
          </h1>

          <div className="flex items-center gap-2 text-gray-600">
            <i className="ri-calendar-line"></i>
            <p className="text-lg">
              <strong>Effective Date:</strong> January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Important Notice */}
          <div className="bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20 rounded-2xl border-2 border-[#66b3ff]/30 p-8 mb-12">
            <p className="text-lg text-gray-800 leading-relaxed font-semibold mb-4">
              Please read the following carefully as they affect your legal rights.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              This set of terms includes a class action waiver and requires binding arbitration on an individual basis to resolve disputes. Your use of the Site (defined below) constitutes your agreement to follow and be bound by this set of terms.
            </p>
          </div>

          {/* Terms Content */}
          <div className="prose max-w-none">
            {/* Section 1 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Introduction</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  Welcome to MandaPost! Please read the following terms and conditions ("Terms of Use") that govern your access to and use of www.mandapost.com and each webpage that links to these Terms of Use ("Site").
                </p>
                <p>
                  If you are a user of MandaPost's web application (collectively, "MandaPost Platform"), please note that your use of the MandaPost Platform is governed by our Terms of Service or other agreement between you and MandaPost that govern your use of the MandaPost Platform, as applicable.
                </p>
                <p>
                  Any reference to "MandaPost", "Osem Dynamics", "our", "us" or "we" means Osem Dynamics LLC. Any reference to "your" or "you" means you as a visitor to the Site.
                </p>
                <p>
                  By using and accessing the Site, you acknowledge that you have read, understood and agree to be bound by these Terms of Use, our Privacy Policy, and our Cookie Notice. If you do not agree to be bound by the Terms of Use, Privacy Policy, or Cookie Notice, then you must not access or use the Site.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Modifications</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  We reserve the right to modify our Terms of Use at any time by posting the modified terms on the Site and without providing you with prior notice. Your continued use of the Site shall be deemed to constitute acceptance by you of such modifications. The updated Terms of Use will be effective as of the time of posting, or on such later date as may be specified in the updated Terms of Use and will apply to your use of the Site from that point forward.
                </p>
                <p>
                  The Effective Date set forth above indicates the last date the Terms of Use was updated, so be sure to check back for updates. We may also change or discontinue any aspect, service, or feature of the Site at any time, including but not limited to content, availability, and equipment needed for access and use, without notice.
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Access and Use</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  Subject to compliance with these Terms of Use, you may use the Site and content included in or made available through the Site (e.g., text, graphics, logos, images, sounds, videos, downloads, documents, etc.) ("Content") for your personal, non-commercial purposes, and to learn more about the products and services offered by MandaPost.
                </p>
                <p>
                  You agree that you are solely responsible for any breach of your obligations under these Terms of Use and for the consequences of any such breach. You represent and warrant that your use of the Site or Content is not unlawful or otherwise prohibited by these Terms of Use.
                </p>
                <p>
                  By accessing and/or using the Site or Content, you agree that you will not, and will not permit any third parties on your behalf to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access or attempt to access the Site other than through the interface that is provided to you by MandaPost, unless you have been otherwise permitted to do so in an express agreement signed by an authorized representative of Osem Dynamics LLC</li>
                  <li>Scrape, duplicate, reproduce, copy, republish, license, sell, trade, or resell the Site or Content for any purpose</li>
                  <li>Use the Site in any manner that could disable, overburden, damage, or impair the Site or interfere with any other party's use of the Site</li>
                  <li>Use any robot, spider, or other automatic device, process, or means to access the Site for any purpose</li>
                  <li>Reverse engineer or otherwise attempt to discover any source code</li>
                  <li>Mirror or frame the Site (in whole or in part) on any other website or webpage</li>
                  <li>Introduce any viruses, Trojan horses, worms, logic bombs, or other materials that are malicious or technologically harmful</li>
                  <li>Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Site</li>
                  <li>Attack the Site via a denial-of-service attack or distributed denial-of-service attack</li>
                  <li>Use the Site for any purpose or in any manner that is unlawful or prohibited by our Terms of Use</li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">4</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Feedback</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  You are not required to provide us with any ideas, suggestions, requests, recommendations, proposals, plans, or other comments or materials (collectively, "Feedback"). If you provide Feedback, you acknowledge and agree that any and all Feedback you provide at this Site are deemed to be non-confidential and we are free to use such information on an unrestricted basis.
                </p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">5</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">External Links</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  The Site may include links to third-party websites, which are provided solely as convenience to you. Such links do not imply or constitute any endorsement, sponsorship, recommendation by MandaPost of the third party, third-party's website, or information contained therein.
                </p>
                <p>
                  You acknowledge that MandaPost has no control over these third-party websites and that MandaPost is not responsible for any of the content, links, or changes posted on such websites. MandaPost shall not be liable to you for any damage or loss arising from or in connection with any links to third-party websites.
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">6</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Intellectual Property Rights</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  Except as otherwise provided, the Site and Content are the exclusive property of Osem Dynamics LLC, its licensors, and content providers and are protected by U.S. and international intellectual property laws. All rights, title, and interest not expressly granted with respect to the Site and/or Content are reserved and retained by Osem Dynamics LLC, its licensors, and content providers.
                </p>
                <p>
                  The "MandaPost" name, the term "MandaPost", the MandaPost logo, and all related names, logos, products, and service names, designs, and slogans that currently are or may become trademarks of MandaPost or its affiliates or licensors. You must not use such marks without our prior written consent.
                </p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">7</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Termination</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  We may terminate or suspend these Terms of Use at any time without notice to you. Without limiting the foregoing, we may, in our sole discretion, terminate your access or use of the Site and/or block your future access to the Site for cause, which includes, but is not limited to: (1) our determination that you violated these Terms of Use or other agreements or guidelines which may be associated with your use of the Site, (2) discontinuance or a material modification to the Site, or (3) unexpected technical issues or problems.
                </p>
              </div>
            </div>

            {/* Section 8 - Disclaimer */}
            <div className="mb-12 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">8</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Disclaimer</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p className="uppercase font-semibold">
                  YOUR USE OF THE SITE AND ITS CONTENT AND ANY ITEMS OBTAINED THROUGH THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY REPRESENTATIONS AND WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. NEITHER MANDAPOST NOR ANY PERSON ASSOCIATED WITH MANDAPOST MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE SITE.
                </p>
                <p className="uppercase font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, MANDAPOST HEREBY DISCLAIMS ALL REPRESENTATIONS AND WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, TITLE, AND FITNESS FOR A PARTICULAR PURPOSE.
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">9</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Indemnification</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  To the fullest extent permitted by applicable law, you agree to indemnify, defend, and hold harmless MandaPost, Osem Dynamics LLC and our affiliates, and each of our respective employees, officers, directors, contractors, consultants, equity holders, suppliers, vendors, service providers, parent companies, subsidiaries, affiliates, agents, representatives, predecessors, successors, and assigns (individually and collectively, the "MandaPost Parties"), from and against all actual or alleged claims, damages, awards, judgments, losses, liabilities of every kind, and nature whatsoever, whether known or unknown, that are caused by, arise out of, or are related to (a) your use or misuse of the Site, (b) your violation of these Terms of Use, and/or (c) your violation of any right(s) of any third party.
                </p>
              </div>
            </div>

            {/* Section 10 - Limitation of Liability */}
            <div className="mb-12 bg-red-50 border-2 border-red-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">10</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Limitation of Liability</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p className="uppercase font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL MANDAPOST, OSEM DYNAMICS LLC, ITS AFFILIATES, LICENSORS, AND SERVICE PROVIDERS, AND THEIR RESPECTIVE DIRECTORS, OFFICERS, EMPLOYEES, CONTRACTORS, AGENTS, SUCCESSORS, AND ASSIGNS BE LIABLE FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR OTHER TYPE OF DAMAGES ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OR PERFORMANCE OF THE SITE.
                </p>
                <p className="uppercase font-semibold">
                  NOTWITHSTANDING THIS SECTION, IF MANDAPOST IS DETERMINED TO HAVE ANY LIABILITY TO YOU OR ANY THIRD PARTY FOR ANY LOSS, HARM, OR DAMAGE, YOU AGREE THAT THE MAXIMUM AGGREGATE LIABILITY OF MANDAPOST AND ITS AFFILIATES SHALL, IN ALL CASES, BE LIMITED TO FIFTY ($50) U.S. DOLLARS.
                </p>
              </div>
            </div>

            {/* Section 11 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">11</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Governing Law and Venue</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  All matters relating to the Site and these Terms of Use, and any dispute or claim arising therefrom or related to (in each case, including non-contractual disputes or claims), shall be governed by and construed in accordance with the laws of the United States, without giving effect to any choice or conflict of law provision or rule.
                </p>
                <p>
                  Except as provided in Section 12 below, any legal suit, action, or proceeding arising out of or related to the Site or these Terms of Use shall be instituted exclusively in the courts having jurisdiction over Osem Dynamics LLC.
                </p>
              </div>
            </div>

            {/* Section 12 - Arbitration */}
            <div className="mb-12 bg-blue-50 border-2 border-blue-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">12</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Arbitration</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  You and MandaPost agree to resolve any dispute or claim arising from or relating to your use of the Site or the Terms of Use through confidential, final and binding arbitration, administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules then in effect.
                </p>
                <p>
                  <strong>You may only bring claims in your individual capacity and on your own behalf, and no claims you bring may be joined or consolidated with any claims brought by any other party.</strong> You further understand that by agreeing to the Terms of Use, you expressly waive your right to a jury trial, to participate in or bring class, collective, or mass action, private attorney general action, or any other proceeding brought against MandaPost by someone else.
                </p>
              </div>
            </div>

            {/* Section 13 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">13</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Copyrights/DMCA Takedown</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  As MandaPost asks others to respect its intellectual property rights, it respects the intellectual property rights of others. MandaPost, in appropriate circumstances, may remove from the Site content that might infringe the intellectual property rights of others.
                </p>
                <p>
                  If you believe your work has been used or copied in a way that constitutes copyright infringement and such infringement is occurring on the Site, you may submit a notification pursuant to the Digital Millennium Copyright Act ("DMCA") by providing our Designated Agent with the following information in writing:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>An electronic or physical signature of the person authorized to act on behalf of the owner of the copyright's interest</li>
                  <li>A description of the copyrighted work that you claim has been infringed</li>
                  <li>Identification of the URL or other specific location on the Site where the material is located</li>
                  <li>Your address, telephone number, and e-mail address</li>
                  <li>A statement by you that you have a good faith belief that the disputed use is not authorized</li>
                  <li>A statement by you, made under penalty of perjury, that the above information is accurate</li>
                </ul>
                <p>
                  You can contact our Designated Agent by emailing <a href="mailto:legal@mandapost.com" className="text-[#22577A] hover:text-[#38a3a5] font-semibold">legal@mandapost.com</a>
                </p>
              </div>
            </div>

            {/* Section 14 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">14</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Miscellaneous</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  <strong>Entire Agreement.</strong> These Terms of Use constitute the sole and entire agreement between you and MandaPost with respect to the subject matter hereof.
                </p>
                <p>
                  <strong>Waiver.</strong> No waiver by MandaPost of any breach or default hereunder shall be deemed to be a waiver of any preceding or subsequent breach or default.
                </p>
                <p>
                  <strong>Severability.</strong> If any provision of these Terms of Use is held to be invalid, unenforceable, or illegal for any reason, such provision shall be limited to the minimum extent such that the remaining provisions will continue in full force and effect.
                </p>
                <p>
                  <strong>Assignability.</strong> These Terms of Use are personal to you, and are not assignable, transferable, or sublicensable by you except with MandaPost's prior written consent.
                </p>
              </div>
            </div>

            {/* Section 15 */}
            <div className="mb-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">15</span>
                </div>
                <h2 className="text-2xl font-bold text-[#22577A] mt-1">Contact Us</h2>
              </div>
              <div className="ml-14 text-gray-700 leading-relaxed space-y-4">
                <p>
                  If you have any questions about our Site or these Terms of Use, please email us at <a href="mailto:legal@mandapost.com" className="text-[#22577A] hover:text-[#38a3a5] font-semibold">legal@mandapost.com</a>
                </p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-question-line text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Have Questions About These Terms?</h3>
                <p className="text-white/90 mb-4">
                  If you have any questions about these Terms of Use, please don't hesitate to contact our legal team.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
